import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ResolvedTheme, ThemePreference, applyTheme, readPreference, resolveTheme, savePreference, systemPrefersDark,
} from './theme';

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readPreference());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: { matches: boolean }) => setSystemDark(e.matches);
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    // Safari before 14 only has the deprecated addListener / removeListener.
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  const resolved = resolveTheme(preference, systemDark);
  useEffect(() => { applyTheme(resolved); }, [resolved]);

  const setPreference = useCallback((pref: ThemePreference) => {
    savePreference(pref);
    setPreferenceState(pref);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** The applied theme, or 'light' outside a provider (safe for shared components rendered in tests). */
export function useResolvedTheme(): ResolvedTheme {
  return useContext(ThemeContext)?.resolved ?? 'light';
}
